// Market V2 adapter: strict regions, sorting, footnotes, detection

export const ALLOWED_REGIONS = ['North America', 'Europe', 'East Asia', 'Southeast Asia', 'Oceania', 'LatAm', 'Middle East', 'Africa'];

export function validateRegions(regionalDistribution) {
  const allowed = new Set(ALLOWED_REGIONS);
  const entries = Object.entries(regionalDistribution || {});
  const unknown = entries.map(([region]) => region).filter((r) => !allowed.has(r));
  return { isValid: unknown.length === 0, unknownRegions: unknown };
}

export function buildMarketDistribution(data, options = {}) {
  const { allowAnyRegions = false } = options;
  const { RegionalDistribution } = data || {};
  if (!allowAnyRegions) {
    const { isValid, unknownRegions } = validateRegions(RegionalDistribution);
    if (!isValid) return { unsupported: true, unknown: unknownRegions };
  }

  const list = Object.entries(RegionalDistribution || {})
    .map(([region, info]) => ({ region, percentage: Number(info?.Share) || 0 }))
    .sort((a, b) => b.percentage - a.percentage);

  const inside = list.filter((x) => x.percentage >= 4);
  const footnotes = list.filter((x) => x.percentage < 4);

  return { inside, footnotes, all: list };
}

export function buildCagrData(data) {
  const rows = [];
  // Prepare references for weighted averages
  const growth = data?.RegionalGrowth || {};
  const distribution = data?.RegionalDistribution || {};

  const computeWeighted = (field) => {
    const entries = Object.entries(growth)
      .map(([region, g]) => {
        const share = Number(distribution?.[region]?.Share) || 0;
        const value = Number(g?.[field]);
        return { share, value };
      })
      .filter((e) => Number.isFinite(e.value) && e.share > 0);
    const totalShare = entries.reduce((acc, e) => acc + e.share, 0);
    if (!totalShare) return undefined;
    const weighted = entries.reduce((acc, e) => acc + e.value * e.share, 0) / totalShare;
    return Number.isFinite(weighted) ? Number(weighted.toFixed(1)) : undefined;
  };

  if (typeof data?.CAGR === 'number') {
    const weightedBase = computeWeighted('CAGR_2024_2030_Base');
    const weightedDownside = computeWeighted('CAGR_2024_2030_Downside');
    const baseDesc = (data?.DriversList && data.DriversList[0]?.text) || 'Weighted by regional base-case growth';
    const downsideDesc = (data?.ChallengesList && data.ChallengesList[0]?.text) || 'Weighted by regional downside-case growth';

    rows.push({
      region: 'Global',
      marketSize: Number(data?.CurrentMarketSize),
      cagr2019: `${data.CAGR}%`,
      baseCase: Number.isFinite(weightedBase) ? `${weightedBase}%` : '—',
      downsideCase: Number.isFinite(weightedDownside) ? `${weightedDownside}%` : '—',
      baseCaseDesc: baseDesc,
      downsideDesc
    });
  }

  // Regional rows from payload

  const regionalRows = Object.entries(growth)
    .map(([region, g]) => ({
      region,
      share: Number(distribution?.[region]?.Share) || 0,
      marketSize: Number(distribution?.[region]?.MarketSize),
      cagr2019: `${g?.CAGR_2019_2024}%`,
      baseCase: `${g?.CAGR_2024_2030_Base}%`,
      downsideCase: `${g?.CAGR_2024_2030_Downside}%`,
      baseCaseDesc: g?.Drivers,
      downsideDesc: g?.Risks
    }))
    // Sort by share descending so the largest regions appear first, matching the left bar
    .sort((a, b) => b.share - a.share)
    // Drop helper field after sorting without triggering unused var lint
    .map((row) => {
      const r = { ...row };
      delete r.share;
      return r;
    });

  return rows.concat(regionalRows);
}

export function formatCurrentMarketSizeToTrillions(value) {
  const num = Number(value);
  if (!Number.isFinite(num)) return '';
  return `${(num / 1_000_000_000_000).toFixed(2)}T`;
}

export function isMarketV2Data(data) {
  try {
    if (data?.schemaVersion === 'market.v2' || data?.format === 'market_v2') return true;
    if (typeof data?.CurrentMarketSize !== 'number') return false;
    if (typeof data?.CAGR !== 'number') return false;

    const dist = data?.RegionalDistribution;
    const growth = data?.RegionalGrowth;

    const distOk =
      dist &&
      typeof dist === 'object' &&
      Object.values(dist).length >= 3 &&
      Object.values(dist).every((r) => typeof r?.Share === 'number' && typeof r?.MarketSize === 'number');

    const growthOk =
      growth &&
      typeof growth === 'object' &&
      Object.values(growth).length >= 3 &&
      Object.values(growth).every(
        (g) =>
          typeof g?.CAGR_2019_2024 === 'number' &&
          typeof g?.CAGR_2024_2030_Base === 'number' &&
          typeof g?.CAGR_2024_2030_Downside === 'number' &&
          typeof g?.Drivers === 'string' &&
          typeof g?.Risks === 'string'
      );

    if (!(distOk && growthOk)) return false;

    const { isValid } = validateRegions(dist);
    return isValid;
  } catch {
    return false;
  }
}
